import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global keyboard shortcuts for the application.
 *
 * Navigation shortcuts (go to page):
 *   g then d  → Dashboard
 *   g then l  → Loan List
 *   g then b  → Borrowers / Board (b then b for board)
 *   g then r  → Reports
 *   g then s  → Settings
 *   g then c  → Collections
 *   g then i  → Data Import
 *
 * Actions:
 *   Cmd/Ctrl+K  → Command Palette (handled by CommandPalette)
 *   ?           → Show keyboard shortcuts help
 *   n then l    → New Loan (navigates to loans page and triggers create)
 *   n then b    → New Borrower (navigates to borrowers page and triggers create)
 */

type ShortcutCallback = () => void;

let pendingPrefix: string | null = null;
let prefixTimer: ReturnType<typeof setTimeout> | null = null;

export function useKeyboardShortcuts(callbacks?: {
  onShowHelp?: ShortcutCallback;
  onNewLoan?: ShortcutCallback;
  onNewBorrower?: ShortcutCallback;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // Ignore when typing in inputs/textareas/selects
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      // Ignore if modifier keys are held (except for Cmd+K which is handled by CommandPalette)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key;

      // ? → Show keyboard shortcuts help
      if (key === '?' && !pendingPrefix) {
        e.preventDefault();
        callbacks?.onShowHelp?.();
        return;
      }

      // Handle two-key sequences
      if (pendingPrefix === 'g') {
        clearPendingPrefix();
        e.preventDefault();
        switch (key) {
          case 'd':
            navigate('/dashboard');
            break;
          case 'l':
            navigate('/loans');
            break;
          case 'b':
            navigate('/borrowers');
            break;
          case 'k':
            navigate('/loans/board');
            break;
          case 'r':
            navigate('/reports');
            break;
          case 's':
            navigate('/settings');
            break;
          case 'c':
            navigate('/collections');
            break;
          case 'i':
            navigate('/import');
            break;
          case 'a':
            navigate('/activity');
            break;
        }
        return;
      }

      if (pendingPrefix === 'n') {
        clearPendingPrefix();
        e.preventDefault();
        switch (key) {
          case 'l':
            callbacks?.onNewLoan?.();
            break;
          case 'b':
            callbacks?.onNewBorrower?.();
            break;
        }
        return;
      }

      // Set prefix for two-key sequences
      if (key === 'g' || key === 'n') {
        e.preventDefault();
        pendingPrefix = key;
        if (prefixTimer) clearTimeout(prefixTimer);
        prefixTimer = setTimeout(clearPendingPrefix, 800);
        return;
      }
    }

    function clearPendingPrefix() {
      pendingPrefix = null;
      if (prefixTimer) {
        clearTimeout(prefixTimer);
        prefixTimer = null;
      }
    }

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearPendingPrefix();
    };
  }, [navigate, callbacks]);
}
